import { useState } from 'react';
import { useSectionAutoGroups, useStudentGroupsList, useDeleteStudentGroup } from '../../../api/hooks';
import { Chip, Button, ConfirmDialog, TEXT_LINK } from '../../../components/ui';
import { FaPlus, FaTrash } from '../../../components/ui/icons';
import StudentGroupForm from './StudentGroupForm';
import { personName } from '../../../lib/names';

interface Props { sectionId: number; canEdit: boolean; }

const BAND_LABELS: Record<string, string> = {
  struggling: 'Struggling',
  on_track:   'On track',
  advanced:   'Excelling',
};
// The design's cutoff labels; the band keys and cutoffs are fixed by the backend.
const BAND_CUTOFF: Record<string, string> = { struggling: '< 50%', on_track: '50–74%', advanced: '≥ 75%' };
const BAND_PILL: Record<string, string> = {
  struggling: 'bg-danger-bg text-danger-ink',
  on_track: 'bg-mist text-muted-2',
  advanced: 'bg-success-bg text-success',
};

interface SeedFromAuto {
  name: string;
  member_ids: number[];
}

export default function GroupsTab({ sectionId, canEdit }: Props) {
  const autoQ = useSectionAutoGroups(sectionId, { weeks: 12, threshold: 50 });
  const groupsQ = useStudentGroupsList(sectionId);
  const [seed, setSeed] = useState<SeedFromAuto | null>(null);
  const [editGroupId, setEditGroupId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function saveBandAsGroup(band: { band: string; students: Array<{ id: number; username: string }> }) {
    setSeed({
      name: `${BAND_LABELS[band.band]} (auto)`,
      member_ids: band.students.map((s) => s.id),
    });
  }
  function saveChapterAsGroup(f: { subject_name: string; chapter_name: string; students: Array<{ id: number }> }) {
    setSeed({
      name: `${f.subject_name} · ${f.chapter_name}`,
      member_ids: f.students.map((s) => s.id),
    });
  }

  const eyebrow = 'font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted';
  return (
    <div className="space-y-6">
      <section aria-label="Auto-groups">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className={eyebrow}>Auto-groups · by overall average</h3>
          <span className="text-xs text-muted">Last 12 weeks · below 50%</span>
        </div>
        {autoQ.isLoading ? <p className="text-muted">Loading…</p> :
         autoQ.isError || !autoQ.data ? <p className="text-danger">Failed to load.</p> :
         <div>
           <div className="grid grid-cols-1 gap-3 min-[1181px]:grid-cols-3">
             {autoQ.data.overall_bands.map((b) => (
               <div key={b.band} className="flex flex-col gap-2.5 rounded-[16px] border border-line p-3.5">
                 <div className="flex items-center justify-between gap-2">
                   <span className="font-semibold text-ink">{BAND_LABELS[b.band] ?? b.band}</span>
                   <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${BAND_PILL[b.band] ?? 'bg-mist text-muted-2'}`}>{BAND_CUTOFF[b.band] ?? b.cutoff}</span>
                 </div>
                 <div className="flex flex-wrap gap-1.5">
                   {b.students.length === 0 ? (
                     <span className="text-xs text-muted">No students.</span>
                   ) : b.students.map((s) => (
                     <span key={s.id} className="inline-flex rounded-full bg-ground px-[9px] py-[3px] text-xs font-medium text-muted-2">{s.name || personName(s)}</span>
                   ))}
                 </div>
                 {canEdit && b.students.length > 0 && (
                   <button type="button" className={`${TEXT_LINK} self-start`} onClick={() => saveBandAsGroup(b)}>
                     Save as group →
                   </button>
                 )}
               </div>
             ))}
           </div>
           {autoQ.data.chapter_focus.length > 0 && (
             <div>
               <h4 className={`mb-2.5 mt-5 ${eyebrow}`}>Chapter focus</h4>
               <div>
                 {autoQ.data.chapter_focus.map((f) => (
                   <div key={`${f.subject_id}-${f.chapter_id}`}
                        className="flex flex-wrap items-center gap-3 border-b border-hairline py-[11px] last:border-b-0">
                     <div className="min-w-0 flex-1 basis-60">
                       <div className="font-semibold text-ink">
                         {f.subject_name} · {f.chapter_name}{' '}
                         <span className="font-normal text-muted">· under {f.threshold_pct}%</span>
                       </div>
                       <div className="text-xs text-muted">
                         {f.students.map((s) => `${s.name || personName(s)} ${s.pct?.toFixed(0)}%`).join(' · ')}
                       </div>
                     </div>
                     {canEdit && (
                       <Button variant="ghost" size="sm" onClick={() => saveChapterAsGroup(f)}>
                         Save as group
                       </Button>
                     )}
                   </div>
                 ))}
               </div>
             </div>
           )}
         </div>}
      </section>

      <section aria-label="Saved groups">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className={eyebrow}>Saved groups</h3>
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<FaPlus size={18} strokeWidth={1.8} />}
              onClick={() => { setShowCreate(true); setSeed(null); }}>
              New group
            </Button>
          )}
        </div>
        {groupsQ.isLoading ? <p className="text-muted">Loading…</p> :
         groupsQ.isError || !groupsQ.data ? <p className="text-danger">Failed to load.</p> :
         groupsQ.data.groups.length === 0 ? <p className="text-sm text-muted">No saved groups yet.</p> :
         <ul className="space-y-2">
           {groupsQ.data.groups.map((g) => (
             <li key={g.id} className="rounded-[12px] border border-line p-3.5">
               <div className="flex flex-wrap items-start justify-between gap-2">
                 <div className="min-w-0">
                   <div className="text-sm font-semibold text-ink">{g.name}</div>
                   <div className="text-xs text-muted">
                     {g.members.length} members · updated {new Date(g.updated_at).toLocaleDateString()}
                   </div>
                 </div>
                 {/* A teacher changes only groups they made (older servers send no can_edit). */}
                 {canEdit && g.can_edit !== false && (
                   <div className="flex gap-2">
                     <Button variant="ghost" size="sm" onClick={() => setEditGroupId(g.id)}>
                       Edit
                     </Button>
                     <DeleteGroupButton sectionId={sectionId} groupId={g.id} />
                   </div>
                 )}
               </div>
               {g.notes && <div className="mt-2 text-xs text-muted">{g.notes}</div>}
               <div className="mt-2 flex flex-wrap gap-1.5">
                 {g.members.map((m) => (
                   <Chip key={m.id} tone="neutral">
                     {m.first_name} {m.last_name}
                   </Chip>
                 ))}
               </div>
             </li>
           ))}
         </ul>}
      </section>

      {(showCreate || seed) && (
        <StudentGroupForm
          sectionId={sectionId}
          seed={seed ?? undefined}
          onClose={() => { setShowCreate(false); setSeed(null); }}
        />
      )}
      {editGroupId !== null && groupsQ.data && (
        <StudentGroupForm
          sectionId={sectionId}
          existing={groupsQ.data.groups.find((g) => g.id === editGroupId)}
          onClose={() => setEditGroupId(null)}
        />
      )}
    </div>
  );
}

function DeleteGroupButton({ sectionId, groupId }: { sectionId: number; groupId: number }) {
  const m = useDeleteStudentGroup(sectionId, groupId);
  const [confirming, setConfirming] = useState(false);
  const err: any = m.error;
  return (
    <>
      {m.isError && (
        <span role="alert" className="self-center text-xs text-danger">
          {err?.response?.data?.detail ?? 'Could not delete this group.'}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon={<FaTrash size={18} strokeWidth={1.8} />}
        onClick={() => setConfirming(true)}>
        Delete
      </Button>
      <ConfirmDialog
        isOpen={confirming}
        title="Delete this group?"
        body="It will be archived."
        confirmLabel="Delete"
        onConfirm={() => { m.mutate(); setConfirming(false); }}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
